import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'motion/react'
import { RecostLogo } from '@/components/RecostLogo'
import { colors, accent } from '@/lib/tokens'

const ease = [0.16, 1, 0.3, 1] as const

// ─── Shared mock data ──────────────────────────────────────────────────────────

const WEEK_BARS = [
  { label: 'Mon', h: 38 },
  { label: 'Tue', h: 52 },
  { label: 'Wed', h: 67 },
  { label: 'Thu', h: 60 },
  { label: 'Fri', h: 92 },
  { label: 'Sat', h: 44 },
  { label: 'Sun', h: 31 },
]

const COST_SERIES = [
  { label: 'Mon', h: 30 }, { label: 'Tue', h: 42 }, { label: 'Wed', h: 55 },
  { label: 'Thu', h: 48 }, { label: 'Fri', h: 80 }, { label: 'Sat', h: 35 }, { label: 'Sun', h: 26 },
]

const PROVIDERS_DETAIL = [
  { name: 'OpenAI',     cost: '$120.21', calls: '8.2k/day', endpoints: 3, pct: 74, color: '#10a37f' },
  { name: 'Anthropic',  cost: '$38.90',  calls: '2.4k/day', endpoints: 1, pct: 20, color: '#d97706' },
  { name: 'Google',     cost: '$11.40',  calls: '0.8k/day', endpoints: 2, pct: 6,  color: '#4285f4' },
]

const ENDPOINTS_DETAIL = [
  { provider: 'OpenAI',    path: '/api/chat',      method: 'POST', calls: '1,204', cost: '$89.12' },
  { provider: 'Anthropic', path: '/api/summarize', method: 'POST', calls: '847',   cost: '$38.90' },
  { provider: 'OpenAI',    path: '/api/embed',     method: 'GET',  calls: '4,211', cost: '$22.18' },
  { provider: 'OpenAI',    path: '/api/classify',  method: 'POST', calls: '3,102', cost: '$8.91'  },
]

const MODEL_DATA = [
  { model: 'gpt-4o',                  cost: '$89.12', pct: 46, color: accent },
  { model: 'claude-3-5-sonnet',       cost: '$38.90', pct: 20, color: '#8b5cf6' },
  { model: 'text-embedding-3-small',  cost: '$22.18', pct: 12, color: '#14b8a6' },
  { model: 'gpt-4o-mini',             cost: '$8.91',  pct: 5,  color: '#3b82f6' },
]

const TOP_ENDPOINTS = [
  { method: 'POST', path: '/api/chat',      cost: '$89.12', pct: 46 },
  { method: 'POST', path: '/api/summarize', cost: '$38.90', pct: 20 },
  { method: 'GET',  path: '/api/embed',     cost: '$22.18', pct: 12 },
  { method: 'POST', path: '/api/classify',  cost: '$8.91',  pct: 5  },
]

const PROVIDER_GROUPS = [
  { name: 'OpenAI',    endpoints: 3, cost: '$120.21', pct: 74, color: '#10a37f' },
  { name: 'Anthropic', endpoints: 1, cost: '$38.90',  pct: 20, color: '#d97706' },
  { name: 'Google',    endpoints: 2, cost: '$11.40',  pct: 6,  color: '#4285f4' },
]

const STATIC_FILES = ['src/routes/chat.ts', 'src/lib/embed.ts', 'src/jobs/classify.ts', 'src/api/summarize.ts']

// ─── Sub-views ─────────────────────────────────────────────────────────────────

function AreaChartMock({ points, labels, color, height = 56, animate: anim }: {
  points: number[]
  labels?: string[]
  color: string
  height?: number
  animate: boolean
}) {
  const svgH = height
  const max = Math.max(...points)
  const min = Math.min(...points) * 0.85
  const range = max - min || 1
  const n = points.length
  const coords = points.map((v, i) => ({
    x: (i / (n - 1)) * 100,
    y: svgH - 4 - ((v - min) / range) * (svgH - 10),
  }))
  const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ')
  const fillPath = `${linePath} L 100 ${svgH} L 0 ${svgH} Z`
  const gradId = `ag${color.replace(/[^a-z0-9]/gi, '')}`
  return (
    <div>
      <svg width="100%" height={svgH} viewBox={`0 0 100 ${svgH}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <motion.path d={fillPath} fill={`url(#${gradId})`} initial={{ opacity: 0 }} animate={anim ? { opacity: 1 } : {}} transition={{ duration: 0.5 }} />
        <motion.path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }} animate={anim ? { pathLength: 1, opacity: 1 } : {}}
          transition={{ duration: 0.8, ease: 'easeInOut' }} />
      </svg>
      {labels && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          {labels.map((l) => <span key={l} style={{ fontSize: 8, color: colors.textMuted }}>{l}</span>)}
        </div>
      )}
    </div>
  )
}

function LiveView({ animate: anim }: { animate: boolean }) {
  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* KPI strip — 4 cols, single bordered container */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', border: `1px solid ${colors.borderDefault}`, borderRadius: 8, overflow: 'hidden', background: colors.bgPage }}>
        {[
          { label: 'Monthly Cost',   value: '$192.60',  color: accent, sub: '+12% vs prior' },
          { label: 'Calls / Day',    value: '9,364',    color: colors.textPrimary, sub: '+4% vs prior'  },
          { label: 'Endpoints',      value: '14',       color: colors.textPrimary, sub: null             },
          { label: 'Avg Cost / Req', value: '$0.0021',  color: accent, sub: null             },
        ].map((s, i) => (
          <div key={s.label} style={{ padding: '10px 13px', borderRight: i < 3 ? '1px solid #e7e5e4' : 'none' }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: colors.textMuted, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: s.color, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 9, color: colors.textMuted, marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Telemetry row + time range */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
          <span style={{ fontSize: 10, color: colors.textMuted }}>Telemetry · last 30 days</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['24h', '7d', '30d'].map((r) => (
            <div key={r} style={{
              padding: '2px 7px', fontSize: 9, fontFamily: 'var(--font-mono)', borderRadius: 4,
              border: r === '30d' ? '1px solid rgba(242,107,29,0.4)' : '1px solid #e7e5e4',
              color: r === '30d' ? '#F26B1D' : '#78716c',
              background: r === '30d' ? 'rgba(242,107,29,0.06)' : 'transparent',
            }}>{r}</div>
          ))}
        </div>
      </div>

      {/* Cost Over Time */}
      <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
        <div style={{ fontSize: 10, color: colors.textMuted, marginBottom: 8 }}>Cost Over Time</div>
        <AreaChartMock points={COST_SERIES.map(b => b.h)} labels={COST_SERIES.map(b => b.label)} color="#F26B1D" height={56} animate={anim} />
      </div>

      {/* Cost by Provider + API Call Volume */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 10, color: colors.textMuted }}>Cost by Provider</span>
            <span style={{ fontSize: 9, color: colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Last 30d</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {PROVIDERS_DETAIL.map((p, i) => (
              <div key={p.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 3 }}>
                  <span style={{ color: colors.textSecondary }}>{p.name}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 500, fontFamily: 'var(--font-mono)' }}>{p.cost}</span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: '#e7e5e4', overflow: 'hidden', marginBottom: 2 }}>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={anim ? { scaleX: p.pct / 100 } : {}}
                    transition={{ duration: 0.6, delay: 0.3 + i * 0.08, ease }}
                    style={{ width: '100%', height: '100%', borderRadius: 2, background: p.color, transformOrigin: 'left' }}
                  />
                </div>
                <span style={{ fontSize: 9, color: colors.textMuted, fontFamily: 'var(--font-mono)' }}>{p.calls} · {p.endpoints} endpoint{p.endpoints !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9, paddingTop: 8, borderTop: `1px solid ${colors.borderDefault}` }}>
            <span style={{ fontSize: 10, color: colors.textSecondary }}>Total</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: accent, fontFamily: 'var(--font-mono)' }}>$192.60</span>
          </div>
        </div>

        <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: colors.textMuted }}>API Call Volume</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e' }} />
              <span style={{ fontSize: 8, color: '#22c55e', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Live</span>
            </div>
          </div>
          <AreaChartMock points={WEEK_BARS.map(b => b.h)} labels={WEEK_BARS.map(b => b.label)} color="#F26B1D" height={56} animate={anim} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${colors.borderDefault}` }}>
            <span style={{ fontSize: 9, color: colors.textMuted }}>Daily avg</span>
            <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: colors.textSecondary }}>9,364 calls</span>
          </div>
        </div>
      </div>

      {/* Endpoints table */}
      <div style={{ border: `1px solid ${colors.borderDefault}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '7px 12px', background: colors.bgPage, borderBottom: `1px solid ${colors.borderDefault}` }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: colors.textPrimary }}>Endpoints</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '72px 1.4fr 46px 64px 58px', padding: '5px 12px', background: colors.bgSubtle, borderBottom: `1px solid ${colors.borderDefault}` }}>
          {['Provider', 'Endpoint', 'Method', 'Calls/day', 'Cost'].map((h) => (
            <span key={h} style={{ fontSize: 9, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{h}</span>
          ))}
        </div>
        {ENDPOINTS_DETAIL.map((ep, i) => (
          <div key={ep.path} style={{
            display: 'grid', gridTemplateColumns: '72px 1.4fr 46px 64px 58px',
            padding: '6px 12px', alignItems: 'center',
            borderBottom: i < ENDPOINTS_DETAIL.length - 1 ? '1px solid #e7e5e4' : 'none',
          }}>
            <span style={{ fontSize: 9, color: colors.textSecondary }}>{ep.provider}</span>
            <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{ep.path}</span>
            <span style={{
              fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'inline-block',
              padding: '1px 4px', borderRadius: 3,
              color: ep.method === 'POST' ? '#d97706' : '#0ea5e9',
              background: ep.method === 'POST' ? 'rgba(217,119,6,0.1)' : 'rgba(14,165,233,0.1)',
              border: ep.method === 'POST' ? '1px solid rgba(217,119,6,0.2)' : '1px solid rgba(14,165,233,0.2)',
            }}>{ep.method}</span>
            <span style={{ fontSize: 10, color: colors.textSecondary }}>{ep.calls}</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: accent, fontFamily: 'var(--font-mono)' }}>{ep.cost}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function AnalyticsView({ animate: anim }: { animate: boolean }) {
  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Control bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', gap: 3 }}>
          {['7d', '30d', '90d'].map((p) => (
            <div key={p} style={{
              padding: '3px 8px', fontSize: 9, fontFamily: 'var(--font-mono)', borderRadius: 4, cursor: 'default',
              border: p === '30d' ? '1px solid rgba(242,107,29,0.4)' : '1px solid #e7e5e4',
              color: p === '30d' ? '#F26B1D' : '#78716c',
              background: p === '30d' ? 'rgba(242,107,29,0.06)' : '#fafaf9',
            }}>{p}</div>
          ))}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 4, fontSize: 9,
          border: `1px solid ${colors.borderDefault}`, color: colors.textMuted, background: colors.bgPage,
        }}>
          <div style={{ width: 22, height: 12, borderRadius: 6, background: '#e7e5e4', position: 'relative' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
          </div>
          Compare to prior period
        </div>
      </div>

      {/* Cost Trend */}
      <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 10, color: colors.textMuted }}>Cost Trend</span>
          <span style={{ fontSize: 9, color: colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Last 30 days</span>
        </div>
        <AreaChartMock points={COST_SERIES.map(b => b.h)} labels={COST_SERIES.map(b => b.label)} color="#F26B1D" height={52} animate={anim} />
      </div>

      {/* Request Volume */}
      <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 10, color: colors.textMuted }}>Request Volume</span>
          <span style={{ fontSize: 9, color: colors.textMuted }}>drag to zoom</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 44 }}>
          {WEEK_BARS.map((bar, i) => (
            <motion.div
              key={bar.label}
              initial={{ scaleY: 0 }}
              animate={anim ? { scaleY: 1 } : {}}
              transition={{ duration: 0.4, delay: i * 0.04, ease }}
              style={{
                flex: 1, transformOrigin: 'bottom', height: `${bar.h}%`,
                borderRadius: '2px 2px 0 0',
                background: `rgba(242,107,29,${0.2 + (bar.h / 92) * 0.65})`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Cost by Model + Top Endpoints */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 10, color: colors.textMuted }}>Cost by Model</span>
            <span style={{ fontSize: 9, color: colors.textMuted }}>Approx</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MODEL_DATA.map((m, i) => (
              <div key={m.model}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 3 }}>
                  <span style={{ color: colors.textSecondary, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, maxWidth: 100 }}>{m.model}</span>
                  <span style={{ color: colors.textPrimary, fontWeight: 500, fontFamily: 'var(--font-mono)', marginLeft: 6, flexShrink: 0 }}>{m.cost}</span>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: '#e7e5e4', overflow: 'hidden' }}>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={anim ? { scaleX: m.pct / 100 } : {}}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.06, ease }}
                    style={{ width: '100%', height: '100%', borderRadius: 2, background: m.color, transformOrigin: 'left' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 10, color: colors.textMuted }}>Top Endpoints by Spend</span>
            <span style={{ fontSize: 9, color: colors.textMuted }}>Monthly</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {TOP_ENDPOINTS.map((ep, i) => (
              <div key={ep.path}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 3, alignItems: 'center', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                    <span style={{
                      fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 600, flexShrink: 0,
                      padding: '1px 3px', borderRadius: 2,
                      color: ep.method === 'POST' ? '#d97706' : '#0ea5e9',
                      background: ep.method === 'POST' ? 'rgba(217,119,6,0.1)' : 'rgba(14,165,233,0.1)',
                      border: ep.method === 'POST' ? '1px solid rgba(217,119,6,0.2)' : '1px solid rgba(14,165,233,0.2)',
                    }}>{ep.method}</span>
                    <span style={{ color: colors.textSecondary, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{ep.path}</span>
                  </div>
                  <span style={{ color: accent, fontWeight: 600, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{ep.cost}</span>
                </div>
                <div style={{ height: 2, borderRadius: 2, background: '#e7e5e4', overflow: 'hidden' }}>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={anim ? { scaleX: ep.pct / 100 } : {}}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.06, ease }}
                    style={{ width: '100%', height: '100%', borderRadius: 2, background: accent, transformOrigin: 'left' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Optimization Suggestions */}
      <div style={{ border: `1px solid ${colors.borderDefault}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', background: colors.bgPage, borderBottom: `1px solid ${colors.borderDefault}` }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: colors.textPrimary }}>Optimization Suggestions</span>
        </div>
        <div style={{ padding: '10px 12px', background: 'rgba(242,107,29,0.04)', borderLeft: `2px solid ${accent}` }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textPrimary, marginBottom: 3 }}>Switch /api/chat to gpt-4o-mini</div>
          <div style={{ fontSize: 10, color: colors.textSecondary, lineHeight: 1.5 }}>
            Rarely exceeds 200 output tokens. ~14× cheaper at this volume. Est. savings: <strong style={{ color: accent }}>$74/mo</strong>
          </div>
        </div>
      </div>
    </div>
  )
}

function StaticView({ animate: anim }: { animate: boolean }) {
  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Top row: Scan Summary + Cost by Provider */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>

        {/* Scan summary + top cost driver stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: colors.textMuted }}>Monthly Cost</span>
              <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: colors.textMuted }}>Latest scan · today</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: accent, fontFamily: 'var(--font-mono)', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 14 }}>$192.60</div>
            <div style={{ height: 1, background: '#e7e5e4', marginBottom: 12 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr 1px 1fr', alignItems: 'start' }}>
              {[
                { val: '14',     label: 'endpoints' },
                { val: '9,364', label: 'calls/day'  },
                { val: '2',     label: 'high risk', accent: true },
              ].map((s, i) => (
                <>
                  {i > 0 && <div key={`div-${i}`} style={{ background: '#e7e5e4', alignSelf: 'stretch' }} />}
                  <div key={s.label} style={{ paddingLeft: i > 0 ? 12 : 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)', color: s.accent ? '#F26B1D' : '#0a0a0a', lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: s.accent ? '#F26B1D' : '#78716c', marginTop: 4 }}>{s.label}</div>
                  </div>
                </>
              ))}
            </div>
          </div>

          {/* Top cost driver */}
          <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: colors.textMuted, marginBottom: 8 }}>Top Cost Driver</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600, flexShrink: 0,
                padding: '2px 5px', borderRadius: 3,
                color: '#d97706', background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.2)',
              }}>POST</span>
              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: colors.textPrimary, flex: 1 }}>/api/chat</span>
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: accent, flexShrink: 0 }}>$89.12<span style={{ fontSize: 9, fontWeight: 400 }}>/mo</span></span>
            </div>
            <div style={{ fontSize: 10, color: accent, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              Switch to cheaper-model — save ~$74/mo
            </div>
          </div>
        </div>

        {/* Cost by provider */}
        <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, color: colors.textMuted, marginBottom: 10 }}>Cost by Provider</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PROVIDERS_DETAIL.map((p, i) => (
              <div key={p.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 4 }}>
                  <span style={{ color: colors.textSecondary }}>{p.name}</span>
                  <span style={{ fontWeight: 500, color: colors.textPrimary, fontFamily: 'var(--font-mono)' }}>{p.cost}</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: '#e7e5e4', overflow: 'hidden', marginBottom: 3 }}>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={anim ? { scaleX: p.pct / 100 } : {}}
                    transition={{ duration: 0.6, delay: 0.2 + i * 0.08, ease }}
                    style={{ width: '100%', height: '100%', borderRadius: 2, background: p.color, transformOrigin: 'left' }}
                  />
                </div>
                <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: colors.textMuted }}>{p.endpoints} endpoint{p.endpoints !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Provider groups */}
      <div style={{ border: `1px solid ${colors.borderDefault}`, borderRadius: 8, overflow: 'hidden' }}>
        {PROVIDER_GROUPS.map((pg, i) => (
          <div key={pg.name} style={{ position: 'relative', borderBottom: i < PROVIDER_GROUPS.length - 1 ? '1px solid #e7e5e4' : 'none' }}>
            {/* Proportional cost fill */}
            <div style={{ position: 'absolute', inset: 0, width: `${pg.pct}%`, background: pg.color, opacity: 0.06, pointerEvents: 'none' }} />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px' }}>
              <div style={{ width: 7, height: 7, borderRadius: 2, background: pg.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 500, color: colors.textPrimary, flex: 1 }}>{pg.name}</span>
              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: colors.textMuted }}>{pg.endpoints} endpoint{pg.endpoints !== 1 ? 's' : ''}</span>
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-mono)', color: accent, marginLeft: 12 }}>{pg.cost}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#78716c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
        ))}
      </div>

      {/* Scanned files */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#78716c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: colors.textMuted }}>Scanned files</span>
          <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: colors.textMuted }}>{STATIC_FILES.length} files</span>
        </div>
        <div style={{ border: `1px solid ${colors.borderDefault}`, borderRadius: 8, overflow: 'hidden' }}>
          {STATIC_FILES.map((f, i) => (
            <div key={f} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px',
              borderBottom: i < STATIC_FILES.length - 1 ? '1px solid #e7e5e4' : 'none',
              background: colors.bgPage,
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#78716c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: colors.textSecondary }}>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function DashboardSidebar() {
  return (
    <div style={{ borderRight: `1px solid ${colors.borderDefault}`, padding: '16px 0', background: colors.bgPage, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 14px', marginBottom: 20 }}>
        <RecostLogo size={17} />
      </div>
      {[
        { label: 'Getting started', active: false },
        { label: 'Projects', active: true },
        { label: 'Account', active: false },
      ].map((item) => (
        <div
          key={item.label}
          style={{
            padding: '6px 14px',
            fontSize: 12,
            
            color: item.active ? '#0a0a0a' : '#78716c',
            fontWeight: item.active ? 500 : 400,
            background: item.active ? 'rgba(242,107,29,0.06)' : 'transparent',
            borderLeft: item.active ? '2px solid #F26B1D' : '2px solid transparent',
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  )
}

// ─── Tab bar ─────────────────────────────────────────────────────────────────

type Tab = 'Live' | 'Static' | 'Analytics'

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const tabs: Tab[] = ['Static', 'Live', 'Analytics']

  return (
    <div style={{ display: 'inline-flex', gap: 20 }}>
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onChange(tab)}
          style={{
            position: 'relative',
            fontSize: 13,
            fontWeight: 500,
            color: active === tab ? '#0a0a0a' : '#7a7a7a',
            padding: '0 0 10px 0',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'color 0.15s',
          }}
        >
          {tab}
          {active === tab && (
            <span style={{ position: 'absolute', inset: 'auto 0 -1px 0', height: 2, background: accent, borderRadius: 0 }} />
          )}
        </button>
      ))}
    </div>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function DashboardPreview() {
  const containerRef = useRef<HTMLDivElement>(null)
  const inView = useInView(containerRef, { once: true, margin: '-80px' })
  const [activeTab, setActiveTab] = useState<Tab>('Static')

  return (
    <section
      style={{
        backgroundColor: colors.bgSubtle,
        borderTop: `1px solid ${colors.borderDefault}`,
        borderBottom: `1px solid ${colors.borderDefault}`,
        padding: '120px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="glow-orb" style={{ width: 500, height: 500, top: -100, left: '50%', transform: 'translateX(-50%)' }} />
      <div className="glow-orb-teal" style={{ width: 400, height: 400, bottom: -80, left: '8%' }} />
      <div className="glow-orb" style={{ width: 300, height: 300, bottom: 0, right: '6%' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div ref={containerRef} style={{ textAlign: 'center', marginBottom: 56 }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, ease }}
            style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: accent, marginBottom: 12 }}
          >
            Dashboard
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.06, ease }}
            style={{ fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 700, letterSpacing: '-0.02em', color: colors.textPrimary, margin: '0 0 12px' }}
          >
            One dashboard for every AI dollar
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.1, ease }}
            style={{ fontSize: 15, color: colors.textSecondary, margin: 0 }}
          >
            Live telemetry, static scans, and model-level analytics — all in one place.
          </motion.p>
        </div>

        {/* App frame */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.16, ease }}
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, borderRadius: 12, boxShadow: '0 8px 48px rgba(0,0,0,0.08)', overflow: 'hidden' }}
        >
          {/* Browser chrome */}
          <div style={{ background: colors.bgSubtle, borderBottom: `1px solid ${colors.borderDefault}`, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', gap: 5 }}>
              {[0, 1, 2].map((i) => <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: '#e7e5e4' }} />)}
            </div>
            <div style={{ flex: 1, marginLeft: 8, background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, borderRadius: 4, padding: '3px 10px', fontSize: 10, color: colors.textMuted, fontFamily: 'var(--font-mono)' }}>
              recost.dev/dashboard/projects/proj_abc123
            </div>
          </div>

          {/* Dashboard body */}
          <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr' }} className="dash-body">
            {/* Sidebar */}
            <DashboardSidebar />

            {/* Main */}
            <div style={{ display: 'flex', flexDirection: 'column', height: 620, overflow: 'hidden' }}>
              {/* Project title row */}
              <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', color: colors.textPrimary, margin: 0 }}>
                    my-saas-app
                  </h3>
                </div>
                <TabBar active={activeTab} onChange={setActiveTab} />
              </div>
              <div style={{ borderBottom: `1px solid ${colors.borderDefault}` }} />

              {/* Tab content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  style={{ paddingBottom: 8 }}
                >
                  {activeTab === 'Live' && <LiveView animate={inView} />}
                  {activeTab === 'Analytics' && <AnalyticsView animate={inView} />}
                  {activeTab === 'Static' && <StaticView animate={inView} />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .dash-body { grid-template-columns: 1fr !important; }
          .dash-body > :first-child { display: none !important; }
        }
      `}</style>
    </section>
  )
}

// ─── Mobile variant ──────────────────────────────────────────────────────────
// A pared-down preview for phones: browser chrome, two-stat KPI strip, cost
// chart, and a three-row provider list. Shows the *gist* of the product
// without trying to render every panel at a size no one can read.

export function MobileDashboardPreview() {
  const containerRef = useRef<HTMLDivElement>(null)
  const inView = useInView(containerRef, { once: true, margin: '-60px' })

  return (
    <section
      style={{
        backgroundColor: colors.bgSubtle,
        borderTop: `1px solid ${colors.borderDefault}`,
        borderBottom: `1px solid ${colors.borderDefault}`,
        padding: '72px 16px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="glow-orb" style={{ width: 280, height: 280, top: -60, left: '50%', transform: 'translateX(-50%)' }} />

      <div style={{ maxWidth: 480, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div ref={containerRef} style={{ textAlign: 'left', marginBottom: 28 }}>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, ease }}
            style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: accent, marginBottom: 10 }}
          >
            Dashboard
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.06, ease }}
            style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, color: colors.textPrimary, margin: '0 0 10px' }}
          >
            One dashboard for every AI dollar
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.1, ease }}
            style={{ fontSize: 14, color: colors.textSecondary, margin: 0, lineHeight: 1.55 }}
          >
            Live telemetry, static scans, and model-level analytics — all in one place.
          </motion.p>
        </div>

        {/* App frame */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.16, ease }}
          style={{
            background: colors.bgBase,
            border: `1px solid ${colors.borderDefault}`,
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {/* Browser chrome */}
          <div style={{ background: colors.bgSubtle, borderBottom: `1px solid ${colors.borderDefault}`, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {[0, 1, 2].map((i) => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#e7e5e4' }} />)}
            </div>
            <div style={{ flex: 1, background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, borderRadius: 4, padding: '3px 9px', fontSize: 10, color: colors.textMuted, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              recost.dev/dashboard
            </div>
          </div>

          {/* Project title + live pulse */}
          <div style={{ padding: '14px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em', color: colors.textPrimary, margin: 0 }}>
              my-saas-app
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <motion.div
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }}
              />
              <span style={{ fontSize: 10, color: colors.textMuted, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>LIVE</span>
            </div>
          </div>

          <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Two headline KPIs side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: `1px solid ${colors.borderDefault}`, borderRadius: 8, overflow: 'hidden', background: colors.bgPage }}>
              <div style={{ padding: '12px 14px', borderRight: `1px solid ${colors.borderDefault}` }}>
                <div style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: colors.textMuted, marginBottom: 5 }}>Monthly Cost</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: accent, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', lineHeight: 1 }}>$192.60</div>
                <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 4 }}>+12% vs prior</div>
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 9, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: colors.textMuted, marginBottom: 5 }}>Calls / Day</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: colors.textPrimary, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', lineHeight: 1 }}>9,364</div>
                <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 4 }}>+4% vs prior</div>
              </div>
            </div>

            {/* Cost over time — larger chart than on desktop (it's the centerpiece here) */}
            <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Cost Over Time</span>
                <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: accent, fontWeight: 600 }}>30d</span>
              </div>
              <AreaChartMock points={COST_SERIES.map(b => b.h)} labels={COST_SERIES.map(b => b.label)} color="#F26B1D" height={72} animate={inView} />
            </div>

            {/* Cost by provider — three rows with bars */}
            <div style={{ background: colors.bgPage, border: `1px solid ${colors.borderDefault}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: 10 }}>Cost by Provider</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {PROVIDERS_DETAIL.map((p, i) => (
                  <div key={p.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                      <span style={{ color: colors.textSecondary, fontWeight: 500 }}>{p.name}</span>
                      <span style={{ color: colors.textPrimary, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{p.cost}</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: '#e7e5e4', overflow: 'hidden' }}>
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={inView ? { scaleX: p.pct / 100 } : {}}
                        transition={{ duration: 0.6, delay: 0.25 + i * 0.08, ease }}
                        style={{ width: '100%', height: '100%', borderRadius: 2, background: p.color, transformOrigin: 'left' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.borderDefault}` }}>
                <span style={{ fontSize: 11, color: colors.textSecondary }}>Total</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: accent, fontFamily: 'var(--font-mono)' }}>$192.60</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
